import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';

/**
 * A startup's shared data, as its founder manages it (User Stories 596 and 598): the Data view in
 * the prototype workbench and the startup's Data page. Nest `startup-data` module, routes under
 * `/projects/:projectId/data` (owner only).
 */

export type StartupDataRecord = { id: string } & Record<string, unknown>;

export interface StartupDataField {
  name: string;
  type: string;
  required?: boolean;
  values?: string[] | null;
  references?: string | null;
}

export interface StartupDataEntity {
  entity: string;
  fields: StartupDataField[];
  /** False until a published prototype wrote its sample rows: the data is not live yet. */
  seeded: boolean;
  records: number;
  samples: number;
  /** In "Recently deleted" (restorable for 30 days). */
  deleted: number;
  updatedAt: string;
  /** Prototypes (`chef_project_links`) that use it. */
  usedBy: Array<{ id: string; title: string | null }>;
}

export interface StartupDataSettings {
  /** Only a signed-in visitor may change data (default: anyone with a link). */
  signedInWritesOnly: boolean;
  /** The role a visitor's new account gets on sign-up. */
  defaultRole: string;
  /** Signing in to one prototype of the startup signs in to all of them. */
  oneSignIn: boolean;
}

export interface StartupDataRow {
  record: StartupDataRecord;
  sample: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

class StartupDataService {
  async overview(
    projectId: string
  ): Promise<{ entities: StartupDataEntity[]; settings: StartupDataSettings }> {
    return this.call(() => apiClient.get(ApiUrls.startupData(projectId)));
  }

  async records(projectId: string, entity: string, deleted = false): Promise<StartupDataRow[]> {
    const body = await this.call<{ records: StartupDataRow[] }>(() =>
      apiClient.get(
        `${ApiUrls.startupData(projectId)}/entities/${encodeURIComponent(entity)}/records`,
        {
          params: deleted ? { deleted: 'true' } : undefined,
        }
      )
    );
    return body.records;
  }

  /** Adds a record (`id` optional: Nest makes one) or, with `id`, merges into that record. */
  async save(
    projectId: string,
    entity: string,
    record: Record<string, unknown>,
    id?: string
  ): Promise<StartupDataRecord | null> {
    const base = `${ApiUrls.startupData(projectId)}/entities/${encodeURIComponent(entity)}/records`;
    const body = await this.call<{ record: StartupDataRecord | null }>(() =>
      id
        ? apiClient.put(`${base}/${encodeURIComponent(id)}`, { record })
        : apiClient.post(base, { record })
    );
    return body.record;
  }

  async remove(projectId: string, entity: string, id: string): Promise<void> {
    await this.call(() =>
      apiClient.delete(
        `${ApiUrls.startupData(projectId)}/entities/${encodeURIComponent(entity)}/records/${encodeURIComponent(id)}`
      )
    );
  }

  async restore(projectId: string, entity: string, id: string): Promise<void> {
    await this.call(() =>
      apiClient.post(
        `${ApiUrls.startupData(projectId)}/entities/${encodeURIComponent(entity)}/records/${encodeURIComponent(id)}/restore`
      )
    );
  }

  async clearSamples(projectId: string, entity: string): Promise<number> {
    const body = await this.call<{ cleared: number }>(() =>
      apiClient.post(
        `${ApiUrls.startupData(projectId)}/entities/${encodeURIComponent(entity)}/clear-samples`
      )
    );
    return body.cleared;
  }

  /** The entity's live records as a CSV file. */
  async exportCsv(projectId: string, entity: string): Promise<Blob> {
    return this.call(() =>
      apiClient.get(
        `${ApiUrls.startupData(projectId)}/entities/${encodeURIComponent(entity)}/export`,
        {
          responseType: 'blob',
        }
      )
    );
  }

  async users(projectId: string): Promise<StartupDataRow[]> {
    const body = await this.call<{ users: StartupDataRow[] }>(() =>
      apiClient.get(`${ApiUrls.startupData(projectId)}/users`)
    );
    return body.users;
  }

  async setRole(projectId: string, userId: string, role: string): Promise<void> {
    await this.call(() =>
      apiClient.patch(`${ApiUrls.startupData(projectId)}/users/${encodeURIComponent(userId)}`, {
        role,
      })
    );
  }

  async removeUser(projectId: string, userId: string): Promise<void> {
    await this.call(() =>
      apiClient.delete(`${ApiUrls.startupData(projectId)}/users/${encodeURIComponent(userId)}`)
    );
  }

  async saveSettings(
    projectId: string,
    settings: Partial<StartupDataSettings>
  ): Promise<StartupDataSettings> {
    const body = await this.call<{ settings: StartupDataSettings }>(() =>
      apiClient.put(`${ApiUrls.startupData(projectId)}/settings`, settings)
    );
    return body.settings;
  }

  /**
   * The founder's preview token: the prototype running in the editor preview uses it to read and
   * write the startup's live data (`/public/preview/data/...`). `api` is where to call.
   */
  async previewSession(projectId: string): Promise<{ api: string; preview: string }> {
    const body = await this.call<{ token: string }>(() =>
      apiClient.post(`${ApiUrls.startupData(projectId)}/preview-session`)
    );
    return { api: ApiUrls.appApiBase(), preview: body.token };
  }

  private async call<T>(run: () => Promise<{ data: unknown }>): Promise<T> {
    try {
      const response = await run();
      return response.data as T;
    } catch (error: unknown) {
      const data = (error as { response?: { data?: { message?: unknown } } })?.response?.data;
      const message = Array.isArray(data?.message) ? data?.message.join(', ') : data?.message;
      throw new Error(
        typeof message === 'string' && message ? message : 'Something went wrong. Please try again.'
      );
    }
  }
}

export const startupDataService = new StartupDataService();
export default startupDataService;
